import type { CreateOrderRequest, CreateOrderResponse, Language, OrderDTO } from 'ecosystem'
import { parseStoreToBackendOrderLines, paymentShippingFactory } from 'api'
import { DEFAULT_CURRENCY, IS_PRODUCTION, resolveUrlPath } from 'shared-utils'
import type { NextApiRequest, NextApiResponse } from 'next'
import storefrontApi from 'api/storefront-api'
import { getNetPrice, nexiOrderLineFactory } from 'api/utils/nexi/checkout/utils'
import { type NexiCreatePaymentRequest } from 'ecosystem/nexi'
import { nexiApi } from 'api/utils/nexi'
import { type CreateOrderHandlerOpts } from './types'
import { ingridShippingProvider } from './ingridShippingProvider'
import { nshiftShippingProvider } from './nshiftShippingProvider'

const generateCreateNexiOrderPayload = ({
  opts,
  cartDiscountCode,
  items,
  backendOrder
}: Pick<CreateOrderRequest, 'cartDiscountCode' | 'items'> & {
  opts: CreateOrderHandlerOpts
  backendOrder: OrderDTO
}) => {
  const { orderLines, orderAmount, netOrderAmount } = nexiOrderLineFactory(
    items,
    cartDiscountCode,
    opts.paymentProviderConfig?.providerOptions?.nexi?.countryCode,
    opts.buyerVat
  )
  // protocol, host, port must be the same that was used for load nexi checkout widget
  const redirectDomain = IS_PRODUCTION ? opts.redirectDomain : 'http://localhost:3000'

  // `netOrderAmount` rides alongside the wire payload rather than inside it —
  // Nexi's `order` object is sent verbatim and must not grow stray keys.
  const payload = {
    order: {
      items: orderLines,
      amount: orderAmount,
      currency:
        opts.paymentProviderConfig?.providerOptions?.nexi?.purchaseCurrency || DEFAULT_CURRENCY,
      reference: backendOrder.orderNumber || ''
    },
    checkout: {
      integrationType: 'EmbeddedCheckout',
      url: resolveUrlPath(redirectDomain, '/checkout'),
      termsUrl: resolveUrlPath(
        redirectDomain,
        opts.paymentProviderConfig?.providerOptions?.nexi?.termsUrl ?? '/terms'
      ),
      ...(opts.paymentProviderConfig?.providerOptions?.nexi?.merchantTermsUrl
        ? {
            merchantTermsUrl: resolveUrlPath(
              redirectDomain,
              opts.paymentProviderConfig?.providerOptions?.nexi?.merchantTermsUrl
            )
          }
        : {}),
      appearance: {
        textOptions: {
          ...(opts.paymentProviderConfig?.providerOptions?.nexi?.completePaymentButtonText
            ? {
                completePaymentButtonText:
                  opts.paymentProviderConfig?.providerOptions?.nexi?.completePaymentButtonText
              }
            : {})
        },
        displayOptions: {
          showMerchantName:
            opts.paymentProviderConfig?.providerOptions?.nexi?.showMerchantName ?? true,
          showOrderSummary:
            opts.paymentProviderConfig?.providerOptions?.nexi?.showOrderSummary ?? true
        }
      },
      countryCode: opts.paymentProviderConfig?.providerOptions?.nexi?.countryCode || 'SWE',
      charge: true
    }
  } as NexiCreatePaymentRequest

  return { payload, netOrderAmount }
}

const simpleShipping = async (
  req: NextApiRequest,
  res: NextApiResponse<CreateOrderResponse | { error: string }>,
  opts: CreateOrderHandlerOpts
) => {
  const { storeId, items, cartDiscountCode }: CreateOrderRequest = JSON.parse(req.body)
  const locale = (req.cookies.NEXT_LOCALE as Language) || 'sv'

  const backendOrderLines = parseStoreToBackendOrderLines(items, cartDiscountCode)
  const backendOrder = await storefrontApi.orders.createStorefrontOrder({
    storeId,
    backendOrderLines,
    cartDiscountCode,
    customerId: opts.customerId,
    paymentProviderInfo: {
      providerName: 'NEXI'
    }
  })

  const { payload: nexiPayload, netOrderAmount } = generateCreateNexiOrderPayload({
    opts,
    backendOrder,
    items,
    cartDiscountCode
  })

  const shippingInfo = paymentShippingFactory({
    orderAmount: nexiPayload.order.amount,
    netOrderAmount,
    locale,
    opts: opts.shippingProviderConfig?.simpleShipping,
    shippingConfig: opts.shippingProviderConfig,
    customerType: opts.customerType,
    // Market-locked session — see `PaymentNexiDefault`.
    recipientCountryCode: opts.paymentProviderConfig?.providerOptions?.nexi?.countryCode,
    countryCode: opts.paymentProviderConfig?.providerOptions?.nexi?.countryCode,
    buyerVat: opts.buyerVat
  })

  const payload = {
    ...nexiPayload,
    order: {
      ...nexiPayload.order,
      amount: nexiPayload.order.amount + shippingInfo.price,
      items: [
        ...nexiPayload.order.items,
        {
          reference: shippingInfo.id,
          name: shippingInfo.name,
          quantity: 1,
          unit: 'NA',
          unitPrice: 0,
          taxRate: shippingInfo.tax_rate,
          taxAmount: shippingInfo.tax_amount,
          grossTotalAmount: shippingInfo.price,
          netTotalAmount: getNetPrice(shippingInfo.price, shippingInfo.tax_rate)
        }
      ]
    }
  } as NexiCreatePaymentRequest

  const { error, data } = await nexiApi.checkout.createPayment(payload)

  if (error || !data) {
    res.status(400).json({ error: error || 'Unknown error' })
    return
  }

  res.status(200).json({ storefrontOrderResponse: backendOrder, nexiResponse: data })
}

const postnordShipping = async (
  req: NextApiRequest,
  res: NextApiResponse<CreateOrderResponse | { error: string }>,
  opts: CreateOrderHandlerOpts
) => {
  const { storeId, items, cartDiscountCode }: CreateOrderRequest = JSON.parse(req.body)

  const backendOrderLines = parseStoreToBackendOrderLines(items, cartDiscountCode)
  const backendOrder = await storefrontApi.orders.createStorefrontOrder({
    storeId,
    backendOrderLines,
    cartDiscountCode,
    customerId: opts.customerId,
    paymentProviderInfo: {
      providerName: 'NEXI'
    },
    shipping: {
      provider: 'POSTNORD'
    }
  })

  const { payload: nexiPayload } = generateCreateNexiOrderPayload({
    opts,
    backendOrder,
    items,
    cartDiscountCode
  })

  const { error, data } = await nexiApi.checkout.createPayment(nexiPayload)

  if (error || !data) {
    res.status(400).json({ error: error || 'Unknown error' })
    return
  }

  res.status(200).json({
    storefrontOrderResponse: backendOrder,
    nexiResponse: data
  })
}

const ingridShipping = (
  req: NextApiRequest,
  res: NextApiResponse<CreateOrderResponse | { error: string }>,
  opts: CreateOrderHandlerOpts
) => {
  void ingridShippingProvider(req, res, opts, async (ingridResponse) => {
    const { storeId, items, cartDiscountCode }: CreateOrderRequest = JSON.parse(req.body)

    const backendOrderLines = parseStoreToBackendOrderLines(items, cartDiscountCode)
    const backendOrder = await storefrontApi.orders.createStorefrontOrder({
      storeId,
      backendOrderLines,
      cartDiscountCode,
      customerId: opts.customerId,
      paymentProviderInfo: {
        providerName: 'NEXI'
      },
      shipping: {
        provider: 'INGRID',
        providerSessionId: ingridResponse.session.checkout_session_id
      }
    })

    const { payload: nexiPayload } = generateCreateNexiOrderPayload({
      opts,
      backendOrder,
      items,
      cartDiscountCode
    })

    const { error, data } = await nexiApi.checkout.createPayment(nexiPayload)

    if (error || !data) {
      res.status(400).json({ error: error || 'Unknown error' })
      return
    }

    res.status(200).json({
      storefrontOrderResponse: backendOrder,
      nexiResponse: data,
      ingridResponse
    })
  })
}

const nshiftShipping = async (
  req: NextApiRequest,
  res: NextApiResponse<CreateOrderResponse | { error: string }>,
  opts: CreateOrderHandlerOpts
) => {
  const nshiftResponse = await nshiftShippingProvider(req, opts)
  const { storeId, items, cartDiscountCode }: CreateOrderRequest = JSON.parse(req.body)

  const backendOrderLines = parseStoreToBackendOrderLines(items, cartDiscountCode)
  const backendOrder = await storefrontApi.orders.createStorefrontOrder({
    storeId,
    backendOrderLines,
    cartDiscountCode,
    customerId: opts.customerId,
    paymentProviderInfo: {
      providerName: 'NEXI'
    },
    shipping: {
      provider: 'NSHIFT',
      providerSessionId: nshiftResponse.sessionId
    }
  })

  const { payload: nexiPayload } = generateCreateNexiOrderPayload({
    opts,
    backendOrder,
    items,
    cartDiscountCode
  })

  const { error, data } = await nexiApi.checkout.createPayment(nexiPayload)

  if (error || !data) {
    res.status(400).json({ error: error || 'Unknown error' })
    return
  }

  res.status(200).json({
    storefrontOrderResponse: backendOrder,
    nexiResponse: data,
    nshiftResponse
  })
}

export const nexiPaymentProvider = async (
  req: NextApiRequest,
  res: NextApiResponse,
  opts: CreateOrderHandlerOpts
) => {
  const { shippingProviderConfig } = opts

  switch (shippingProviderConfig?.provider ?? 'DEFAULT') {
    case 'INGRID':
      return ingridShipping(req, res, opts)
    case 'POSTNORD':
      return postnordShipping(req, res, opts)
    case 'NSHIFT':
      return nshiftShipping(req, res, opts)
    case 'DEFAULT':
      return simpleShipping(req, res, opts)
  }
}
